import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import addLogDocument from "../addLogDocument.js";
import { getDocDataById } from "../../models/DataFunctions.js";

/**
 * Create a document in a Firestore collection via an API endpoint.
 *
 * @param {string} collection - Firestore collection name.
 * @param {object} data - Data to create in the document (key-value pairs).
 * @param {string} prefix - API endpoint prefix.
 * @returns {Promise<string>} - Returns "true" on success or error details on failure.
 */
const createDocApi = async (collection, data, prefix) => {
  // Define the endpoint URL
  const token = await getOrRefreshFirebaseToken();
  const url = `${prefix}database/create-doc`;

  // Define the request body
  const body = {
    collection,
    data: transformObjectReferences(data),
  };

  // Define headers
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  try {
    const start = new Date();
    // Make the POST request
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    const auth = getAuthCentral();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return "User not logged in";
    }

    const db = getFirestore();
    const userRef = doc(db, "user", currentUser.uid);
    const userSnapshot = await getDoc(checkDB(userRef));
    const responseText = await response.text();
    const end = new Date();
    const elapsed = end - start;
    if (userSnapshot.exists()) {
      const org_id = userSnapshot.get("org_id");

      // Add log document
      await addLogDocument(
        url,
        prefix,
        userRef,
        org_id,
        response.status.toString(),
        "", // Additional optional fields
        "",
        JSON.stringify(body),
        responseText,
        "POST",
        elapsed
      );
    }

    // Handle response
    if (response.ok) {
      const responseData = JSON.parse(responseText);

      let id = responseData.id;
      if (id) {
        let doc = await getDocDataById(collection, id);

        return doc;
      }
    } else {
      console.error(`Failed to create document: ${response.status}`);
      return "";
    }
  } catch (error) {
    console.error(`Exception occurred: ${error.message}`);
    return "";
  }
};

export default createDocApi;

/**
 * Recursively scans an object for Firestore DocumentReferences
 * and transforms them into JSON-compatible formats.
 *
 * @param {any} input - The object or value to check and transform.
 * @param {WeakSet} [visited] - Tracks visited objects to prevent circular references.
 * @returns {any} - A transformed object with DocumentReferences replaced by their JSON-compatible paths.
 */
function transformObjectReferences(input, visited = new WeakSet()) {
  if (input && typeof input === "object") {
    if (visited.has(input)) {
      return null; // Handle circular references
    }
    visited.add(input);
  }

  if (
    input &&
    typeof input === "object" &&
    (input.constructor?.name === "DocumentReference" ||
      input.type === "document") &&
    (input.path || input._key?.path)
  ) {
    return { documentPath: input.path || input._key.path };
  }

  if (Array.isArray(input)) {
    return input.map((item) => transformObjectReferences(item, visited));
  }

  if (input && typeof input === "object") {
    const transformed = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        transformed[key] = transformObjectReferences(input[key], visited);
      }
    }
    return transformed;
  }

  return input;
}
